import React, {useCallback, useEffect, useMemo, useState} from "react";
import {observer} from "mobx-react";
import {Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table, Modal} from "react-bootstrap";
import {useParams} from "react-router-dom";
import HeroSection from "../../../components/HeroSection.jsx";
import {productApi} from "../../../api/product.js";
import {vendorSoftwareApi} from "../../../api/vendorSoftware.js";

function AdminProductDetailsPage() {
    const {productId} = useParams();
    const [product, setProduct] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        productCode: "",
        description: "",
        taxAuthorityId: null,
        vendorSoftwareId: null,
        apiUrl: "",
        apiUsername: "",
        apiPassword: "",
        apiKey: "",
        online: false,
        billable: false
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [saveError, setSaveError] = useState("");
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [vendors, setVendors] = useState([]);
    const [feeForm, setFeeForm] = useState({
        id: null,
        feeType: "ProductFee",
        amount: "",
        tax1: "",
        tax1Name: "",
        tax2: "",
        tax2Name: "",
        effective: "",
        expires: ""
    });
    const [feeStatus, setFeeStatus] = useState({saving: false, error: "", success: ""});
    const [priceDate, setPriceDate] = useState(new Date().toISOString().slice(0, 16));
    const [showFeeModal, setShowFeeModal] = useState(false);

    const loadProduct = useCallback(async () => {
        setIsLoading(true);
        try {
            const details = await productApi.getProduct(productId);
            details.fees = details.fees || [];
            setProduct(details);
            setFormData({
                name: details.name || "",
                productCode: details.productCode || "",
                description: details.description || "",
                taxAuthorityId: details.taxAuthorityId,
                vendorSoftwareId: details.vendorSoftwareId,
                apiUrl: details.apiUrl || "",
                apiUsername: details.apiUsername || "",
                apiPassword: "",
                apiKey: details.apiKey || "",
                online: details.online || false,
                billable: details.billable || false
            });
            setError("");
        } catch (e) {
            console.error("Failed to load product", e);
            setError("Unable to load product details.");
        } finally {
            setIsLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        loadProduct();
    }, [loadProduct]);

    useEffect(() => {
        const loadVendors = async () => {
            try {
                const results = await vendorSoftwareApi.getVendors();
                setVendors(results || []);
            } catch (err) {
                console.error("Failed to load vendors", err);
            }
        };
        loadVendors();
    }, []);

    const friendlyType = (t) => t || "-";

    const handleChange = (field, value) => {
        let nextVal = value;
        if (field === "vendorSoftwareId") {
            const parsed = parseInt(value, 10);
            nextVal = Number.isNaN(parsed) ? null : parsed;
        }
        setFormData((prev) => ({...prev, [field]: nextVal}));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!product) return;
        setIsSaving(true);
        setSaveError("");
        setSaveSuccess(false);
        try {
            await productApi.updateProduct(product.id, {
                name: formData.name,
                productCode: formData.productCode,
                description: formData.description,
                taxAuthorityId: formData.taxAuthorityId,
                vendorSoftwareId: formData.vendorSoftwareId,
                apiUrl: formData.apiUrl,
                apiUsername: formData.apiUsername,
                apiPassword: formData.apiPassword || null,
                apiKey: formData.apiKey,
                online: formData.online,
                billable: formData.billable
            });
            setSaveSuccess(true);
            setFormData((prev) => ({...prev, apiPassword: ""}));
            await loadProduct();
        } catch (saveErr) {
            console.error("Failed to update product", saveErr);
            setSaveError("Failed to update product. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    const resetFeeForm = useCallback(() => {
        setFeeForm({
            id: null,
            feeType: "ProductFee",
            amount: "",
            tax1: "",
            tax1Name: "",
            tax2: "",
            tax2Name: "",
            effective: "",
            expires: ""
        });
    }, []);

    const handleFeeSubmit = async (e) => {
        e.preventDefault();
        if (!product) return;
        setFeeStatus({saving: true, error: "", success: ""});
        const payload = {
            feeType: feeForm.feeType,
            amount: parseFloat(feeForm.amount || 0),
            tax1: feeForm.tax1 !== "" ? parseFloat(feeForm.tax1) : null,
            tax2: feeForm.tax2 !== "" ? parseFloat(feeForm.tax2) : null,
            tax1Name: feeForm.tax1Name || null,
            tax2Name: feeForm.tax2Name || null,
            effective: feeForm.effective,
            expires: feeForm.expires || null
        };
        try {
            if (feeForm.id) {
                await productApi.updateFee(product.id, feeForm.id, payload);
                setFeeStatus({saving: false, error: "", success: "Fee updated."});
            } else {
                await productApi.addFee(product.id, payload);
                setFeeStatus({saving: false, error: "", success: "Fee added."});
            }
            resetFeeForm();
            setShowFeeModal(false);
            await loadProduct();
        } catch (err) {
            console.error("Failed to save fee", err);
            setFeeStatus({saving: false, error: "Failed to save fee.", success: ""});
        }
    };

    const handleFeeEdit = (fee) => {
        setFeeForm({
            id: fee.id,
            feeType: fee.feeType,
            amount: fee.amount,
            tax1: fee.tax1 ?? "",
            tax1Name: fee.tax1Name ?? "",
            tax2: fee.tax2 ?? "",
            tax2Name: fee.tax2Name ?? "",
            effective: fee.effective ? new Date(fee.effective).toISOString().slice(0, 16) : "",
            expires: fee.expires ? new Date(fee.expires).toISOString().slice(0, 16) : ""
        });
        setFeeStatus({saving: false, error: "", success: ""});
        setShowFeeModal(true);
    };

    const handleFeeDelete = async (feeId) => {
        if (!product) return;
        if (!window.confirm("Delete this fee?")) return;
        setFeeStatus({saving: true, error: "", success: ""});
        try {
            await productApi.deleteFee(product.id, feeId);
            setFeeStatus({saving: false, error: "", success: "Fee deleted."});
            await loadProduct();
        } catch (err) {
            console.error("Failed to delete fee", err);
            setFeeStatus({saving: false, error: "Failed to delete fee.", success: ""});
        }
    };

    const vendorOptions = useMemo(() => vendors || [], [vendors]);
    const displayFees = useMemo(() => (product?.fees || []).filter((f) => !f.deleted), [product]);

    const pricePreview = useMemo(() => {
        const selectedDate = priceDate ? new Date(priceDate) : new Date();
        const activeFees = displayFees.filter((fee) => {
            const eff = fee.effective ? new Date(fee.effective) : null;
            const exp = fee.expires ? new Date(fee.expires) : null;
            if (!eff) return false;
            return eff <= selectedDate && (!exp || exp > selectedDate);
        });
        let subTotal = 0;
        let tax1Total = 0;
        let tax2Total = 0;
        activeFees.forEach((fee) => {
            subTotal += fee.amount;
            if (fee.tax1) tax1Total += fee.amount * fee.tax1;
            if (fee.tax2) tax2Total += fee.amount * fee.tax2;
        });
        const total = subTotal + tax1Total + tax2Total;
        return {activeFees, subTotal, tax1Total, tax2Total, total, at: selectedDate};
    }, [displayFees, priceDate]);

    return (
        <Container className="dashboard-shell">
            <HeroSection
                accent="admin"
                eyebrow="Product"
                title={product ? product.name : "Product details"}
                subtitle={product ? product.productCode : ""}
            />
            {isLoading && (
                <div className="d-flex align-items-center my-4">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Loading product...</span>
                </div>
            )}
            {error && <Alert variant="danger" className="my-3">{error}</Alert>}
            {!isLoading && !error && product && (
                <>
                    {saveError && <Alert variant="danger" className="my-3">{saveError}</Alert>}
                    {saveSuccess && <Alert variant="success" className="my-3">Product updated.</Alert>}
                    <Card className="dashboard-panel">
                        <Card.Body>
                            <Form onSubmit={handleSubmit}>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Name</Form.Label>
                                            <Form.Control
                                                value={formData.name}
                                                onChange={(e) => handleChange("name", e.target.value)}
                                                required
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Product Code</Form.Label>
                                            <Form.Control
                                                value={formData.productCode}
                                                onChange={(e) => handleChange("productCode", e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Product Type</Form.Label>
                                            <Form.Control value={friendlyType(product.productType)} readOnly />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Tax Authority</Form.Label>
                                            <Form.Control value={product.taxAuthorityName || "-"} readOnly />
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Vendor Software</Form.Label>
                                            <Form.Select
                                                value={formData.vendorSoftwareId || ""}
                                                onChange={(e) => handleChange("vendorSoftwareId", parseInt(e.target.value))}
                                            >
                                                <option value="">Select vendor</option>
                                                {vendorOptions.map((v) => (
                                                    <option key={v.id} value={v.id}>{v.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        <Form.Group className="mb-3">
                                            <Form.Check
                                                type="switch"
                                                id="online-switch"
                                                label="Online"
                                                checked={formData.online}
                                                onChange={(e) => handleChange("online", e.target.checked)}
                                            />
                                            <Form.Check
                                                type="switch"
                                                id="billable-switch"
                                                label="Billable"
                                                checked={formData.billable}
                                                onChange={(e) => handleChange("billable", e.target.checked)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Form.Group className="mb-3">
                                    <Form.Label>Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={formData.description}
                                        onChange={(e) => handleChange("description", e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>API URL</Form.Label>
                                    <Form.Control
                                        value={formData.apiUrl}
                                        onChange={(e) => handleChange("apiUrl", e.target.value)}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>API Username</Form.Label>
                                    <Form.Control
                                        value={formData.apiUsername}
                                        onChange={(e) => handleChange("apiUsername", e.target.value)}
                                    />
                                </Form.Group>
                                <Row className="mb-3">
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>API Password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Leave blank to keep current"
                                                value={formData.apiPassword}
                                                onChange={(e) => handleChange("apiPassword", e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>API Key</Form.Label>
                                            <Form.Control
                                                value={formData.apiKey}
                                                onChange={(e) => handleChange("apiKey", e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end mt-3">
                                    <Badge bg="light" text="dark" className="me-2">Product Type is read-only</Badge>
                                    <Button variant="primary" type="submit" disabled={isSaving}>
                                        {isSaving ? "Saving..." : "Save changes"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                    <Card className="dashboard-panel mt-3">
                        <Card.Header className="bg-white d-flex justify-content-between">
                            <span>Fees</span>
                            <Badge bg="light" text="dark">{displayFees.length}</Badge>
                        </Card.Header>
                        <Card.Body>
                            <Card className="mb-3 shadow-sm">
                                <Card.Body>
                                    <Row className="g-3 align-items-center">
                                        <Col md={4}>
                                            <Form.Label className="fw-semibold text-muted">Pricing date/time</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={priceDate}
                                                onChange={(e) => setPriceDate(e.target.value)}
                                            />
                                            <div className="small text-muted mt-2 d-flex justify-content-between">
                                                <span>Active fees counted: {pricePreview.activeFees.length}</span>
                                            </div>
                                        </Col>
                                        <Col md={8}>
                                            <div className="d-flex flex-column flex-md-row justify-content-md-end text-md-end gap-3">
                                                <div className="p-2 rounded-3 bg-light">
                                                    <div className="small text-muted">Subtotal</div>
                                                    <div className="fw-bold">${pricePreview.subTotal.toFixed(2)}</div>
                                                </div>
                                                <div className="p-2 rounded-3 bg-light">
                                                    <div className="small text-muted">Tax 1</div>
                                                    <div className="fw-bold">${pricePreview.tax1Total.toFixed(2)}</div>
                                                </div>
                                                <div className="p-2 rounded-3 bg-light">
                                                    <div className="small text-muted">Tax 2</div>
                                                    <div className="fw-bold">${pricePreview.tax2Total.toFixed(2)}</div>
                                                </div>
                                                <div className="p-2 rounded-3 bg-primary text-white">
                                                    <div className="small text-white-50">Total as of {pricePreview.at.toLocaleString()}</div>
                                                    <div className="fw-bold h5 mb-0">${pricePreview.total.toFixed(2)}</div>
                                                </div>
                                            </div>
                                            
                                        </Col>
                                    </Row>
                            </Card.Body>
                        </Card>
                        {feeStatus.error && <Alert variant="danger">{feeStatus.error}</Alert>}
                        {feeStatus.success && <Alert variant="success">{feeStatus.success}</Alert>}
                        <div className="d-flex justify-content-end mb-2">
                            <Button variant="primary" onClick={() => {resetFeeForm(); setShowFeeModal(true);}}>
                                Add Fee
                            </Button>
                        </div>
                            {displayFees.length > 0 ? (
                                <Table hover responsive>
                                    <thead>
                                        <tr>
                                            <th>Amount</th>
                                            <th>Fee Type</th>
                                            <th>Effective</th>
                                            <th>Expires</th>
                                            <th>Tax 1</th>
                                            <th>Tax 2</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayFees.map((fee) => (
                                            <tr key={fee.id}>
                                                <td>${fee.amount.toFixed(2)}</td>
                                                <td>{fee.feeType}</td>
                                                <td>{fee.effective ? new Date(fee.effective).toLocaleDateString() : "-"}</td>
                                                <td>{fee.expires ? new Date(fee.expires).toLocaleDateString() : "-"}</td>
                                                <td>{fee.tax1 ? `${fee.tax1 * 100}% ${fee.tax1Name || ""}` : "-"}</td>
                                                <td>{fee.tax2 ? `${fee.tax2 * 100}% ${fee.tax2Name || ""}` : "-"}</td>
                                                <td>
                                                    <Button size="sm" variant="link" onClick={() => handleFeeEdit(fee)}>Edit</Button>
                                                    <Button size="sm" variant="link" className="text-danger" onClick={() => handleFeeDelete(fee.id)}>Delete</Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <div className="text-muted">No fees configured for this product.</div>
                            )}
                        </Card.Body>
                    </Card>
                    <Modal show={showFeeModal} onHide={() => setShowFeeModal(false)} size="lg" centered>
                        <Modal.Header closeButton>
                            <Modal.Title>{feeForm.id ? "Edit Fee" : "Add Fee"}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {feeStatus.error && <Alert variant="danger">{feeStatus.error}</Alert>}
                            {feeStatus.success && <Alert variant="success">{feeStatus.success}</Alert>}
                            <Form onSubmit={handleFeeSubmit}>
                                <Row className="g-3">
                                    <Col md={4}>
                                        <Form.Label>Amount</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.01"
                                            value={feeForm.amount}
                                            onChange={(e) => setFeeForm({...feeForm, amount: e.target.value})}
                                            required
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Fee Type</Form.Label>
                                        <Form.Select
                                            value={feeForm.feeType}
                                            onChange={(e) => setFeeForm({...feeForm, feeType: e.target.value})}
                                        >
                                            <option value="ProductFee">Product Fee</option>
                                            <option value="ServiceFee">Service Fee</option>
                                        </Form.Select>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Effective</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={feeForm.effective}
                                            onChange={(e) => setFeeForm({...feeForm, effective: e.target.value})}
                                            required
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-3 mt-1">
                                    <Col md={4}>
                                        <Form.Label>Expires</Form.Label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={feeForm.expires}
                                            onChange={(e) => setFeeForm({...feeForm, expires: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Tax 1 Rate</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.0001"
                                            value={feeForm.tax1}
                                            onChange={(e) => setFeeForm({...feeForm, tax1: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Tax 1 Name</Form.Label>
                                        <Form.Control
                                            value={feeForm.tax1Name}
                                            onChange={(e) => setFeeForm({...feeForm, tax1Name: e.target.value})}
                                        />
                                    </Col>
                                </Row>
                                <Row className="g-3 mt-1">
                                    <Col md={4}>
                                        <Form.Label>Tax 2 Rate</Form.Label>
                                        <Form.Control
                                            type="number"
                                            step="0.0001"
                                            value={feeForm.tax2}
                                            onChange={(e) => setFeeForm({...feeForm, tax2: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Form.Label>Tax 2 Name</Form.Label>
                                        <Form.Control
                                            value={feeForm.tax2Name}
                                            onChange={(e) => setFeeForm({...feeForm, tax2Name: e.target.value})}
                                        />
                                    </Col>
                                </Row>
                                <div className="d-flex justify-content-end mt-3">
                                    <Button variant="outline-secondary" className="me-2" onClick={() => {resetFeeForm(); setShowFeeModal(false);}}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={feeStatus.saving}>
                                        {feeStatus.saving ? "Saving..." : feeForm.id ? "Update Fee" : "Add Fee"}
                                    </Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>
                </>
            )}
        </Container>
    );
}

export default observer(AdminProductDetailsPage);
